import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  Button,
  Alert,
  Snackbar,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Settings,
  Storage,
  Security,
  Refresh,
  CloudDownload,
  SystemUpdate,
  Backup,
} from '@mui/icons-material';
import apiService from '../../services/api';

interface SystemInfo {
  version: string;
  environment: string;
  database_status: string;
  last_backup: string;
  disk_usage: number;
  memory_usage: number;
  uptime: string;
}

interface SystemLog {
  id: number;
  level: string;
  message: string;
  timestamp: string;
  source: string;
}

export const AdminSystemPage: React.FC = () => {
  const [systemInfo, setSystemInfo] = useState<SystemInfo | null>(null);
  const [systemLogs, setSystemLogs] = useState<SystemLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [backupDialog, setBackupDialog] = useState(false);
  const [backupType, setBackupType] = useState('full');

  useEffect(() => {
    fetchSystemInfo();
    fetchSystemLogs();
  }, []);

  const fetchSystemInfo = async () => {
    try {
      const response = await apiService.getSystemHealth();
      setSystemInfo(response.data as any);
    } catch (error) {
      console.error('Error fetching system info:', error);
      // Mock data for demo
      setSystemInfo({
        version: '1.0.0',
        environment: 'production',
        database_status: 'connected',
        last_backup: new Date().toISOString(),
        disk_usage: 75,
        memory_usage: 60,
        uptime: '15 days, 3 hours',
      });
    }
  };

  const fetchSystemLogs = async () => {
    try {
      // System logs endpoint doesn't exist yet - using mock data
      // const response = await apiService.getSystemLogs();
      // setSystemLogs((response.data?.logs || []) as any);
      // Mock data for demo
      setSystemLogs([
        {
          id: 1,
          level: 'INFO',
          message: 'System started successfully',
          timestamp: new Date().toISOString(),
          source: 'system',
        },
        {
          id: 2,
          level: 'WARNING',
          message: 'High memory usage detected',
          timestamp: new Date(Date.now() - 3600000).toISOString(),
          source: 'monitor',
        },
        {
          id: 3,
          level: 'ERROR',
          message: 'Database connection timeout',
          timestamp: new Date(Date.now() - 7200000).toISOString(),
          source: 'database',
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleSeedData = async () => {
    try {
      const response = await apiService.seedData();
      const data = response.data;
      setSnackbar({ 
        open: true, 
        message: `Data seeded successfully: ${data.clubs_created} clubs, ${data.users_created} users, ${data.tournaments_created} tournaments`, 
        severity: 'success' 
      });
    } catch (error: any) {
      console.error('Error seeding data:', error);
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.detail || 'Failed to seed data', 
        severity: 'error' 
      });
    }
  };

  const handleResetAllData = async () => {
    if (!window.confirm('Are you sure you want to reset all data? This will delete all non-admin users, matches, tournaments, and scores. This action cannot be undone.')) {
      return;
    }
    try {
      setLoading(true);
      const response = await apiService.resetAllData();
      const data = response.data;
      setSnackbar({ 
        open: true, 
        message: `All data reset successfully. Deleted ${data.deleted?.users || 0} users, ${data.deleted?.matches || 0} matches, ${data.deleted?.tournaments || 0} tournaments.`, 
        severity: 'success' 
      });
      fetchSystemInfo();
    } catch (error: any) {
      console.error('Error resetting data:', error);
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.detail || 'Failed to reset data', 
        severity: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFictiveData = async () => {
    if (!window.confirm('This will create 20 users with 25 matches each (500 total matches) for testing ELO evolution. Continue?')) {
      return;
    }
    try {
      setLoading(true);
      const response = await apiService.createFictiveData(20, 25);
      const data = response.data;
      setSnackbar({ 
        open: true, 
        message: `Fictive data created successfully! ${data.summary?.users || 0} users, ${data.summary?.matches || 0} matches, ${data.summary?.completed_matches || 0} completed matches. Check server logs for ELO evolution details.`, 
        severity: 'success' 
      });
      fetchSystemInfo();
    } catch (error: any) {
      console.error('Error creating fictive data:', error);
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.detail || 'Failed to create fictive data', 
        severity: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBackup = async () => {
    try {
      await apiService.systemBackup(backupType);
      setSnackbar({ open: true, message: 'Backup created successfully', severity: 'success' });
      setBackupDialog(false);
      fetchSystemInfo();
    } catch (error: any) {
      console.error('Error creating backup:', error);
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.detail || 'Failed to create backup', 
        severity: 'error' 
      });
    }
  };

  const handleSystemRestart = async () => {
    if (window.confirm('Are you sure you want to restart the system? This will cause temporary downtime.')) {
      try {
        await apiService.systemRestart();
        setSnackbar({ open: true, message: 'System restart initiated', severity: 'success' });
      } catch (error: any) {
        console.error('Error restarting system:', error);
        setSnackbar({ 
          open: true, 
          message: error.response?.data?.detail || 'Failed to restart system', 
          severity: 'error' 
        });
      }
    }
  };

  const getLogLevelColor = (level: string) => {
    switch (level.toLowerCase()) {
      case 'error':
        return 'error';
      case 'warning':
        return 'warning';
      case 'info':
        return 'info';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        System Management
      </Typography>

      {/* System Status Cards */}
      <Box display="flex" flexDirection={{ xs: "column", sm: "row" }} gap={2}>
        <Box flex={1}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Settings color="primary" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">{systemInfo?.version || 'N/A'}</Typography>
                  <Typography color="textSecondary">Version</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box flex={1}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Storage color="success" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">{systemInfo?.uptime || 'N/A'}</Typography>
                  <Typography color="textSecondary">Uptime</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box flex={1}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Storage color="info" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">{systemInfo?.disk_usage || 0}%</Typography>
                  <Typography color="textSecondary">Disk Usage</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box flex={1}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Security color="warning" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h6">{systemInfo?.memory_usage || 0}%</Typography>
                  <Typography color="textSecondary">Memory Usage</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* System Actions */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          System Actions
        </Typography>
        <Box display="flex" flexDirection={{ xs: "column", sm: "row" }} gap={2} flexWrap="wrap">
          <Box flex={1} minWidth={150}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<Refresh />}
              onClick={handleSeedData}
            >
              Seed Data
            </Button>
          </Box>
          <Box flex={1} minWidth={150}>
            <Button
              fullWidth
              variant="contained"
              color="error"
              startIcon={<Refresh />}
              onClick={handleResetAllData}
            >
              Reset All Data
            </Button>
          </Box>
          <Box flex={1} minWidth={150}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              startIcon={<Refresh />}
              onClick={handleCreateFictiveData}
            >
              Create Fictive Data
            </Button>
          </Box>
          <Box flex={1} minWidth={150}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<Backup />}
              onClick={() => setBackupDialog(true)}
            >
              Create Backup
            </Button>
          </Box>
          <Box flex={1} minWidth={150}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<CloudDownload />}
              color="secondary"
            >
              Download Logs
            </Button>
          </Box>
          <Box flex={1} minWidth={150}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<SystemUpdate />}
              color="warning"
              onClick={handleSystemRestart}
            >
              Restart System
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* System Logs */}
      <Paper sx={{ p: 3 }}>
        <Box display="flex" alignItems="center" gap={2} sx={{ mb: 2 }}>
          <Typography variant="h6">
            System Logs
          </Typography>
          <Chip label="Demo data (backend endpoint not yet implemented)" size="small" color="warning" variant="outlined" />
        </Box>
        <List>
          {systemLogs.map((log, index) => (
            <React.Fragment key={log.id}>
              <ListItem>
                <ListItemIcon>
                  <Chip
                    label={log.level}
                    color={getLogLevelColor(log.level) as any}
                    size="small"
                  />
                </ListItemIcon>
                <ListItemText
                  primary={log.message}
                  secondary={`${log.source} • ${new Date(log.timestamp).toLocaleString()}`}
                />
              </ListItem>
              {index < systemLogs.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>
      </Paper>

      {/* Backup Dialog */}
      <Dialog open={backupDialog} onClose={() => setBackupDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create System Backup</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Backup Type</InputLabel>
            <Select
              value={backupType}
              label="Backup Type"
              onChange={(e) => setBackupType(e.target.value)}
            >
              <MenuItem value="full">Full Backup</MenuItem>
              <MenuItem value="incremental">Incremental Backup</MenuItem>
              <MenuItem value="database">Database Only</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBackupDialog(false)}>Cancel</Button>
          <Button onClick={handleBackup} variant="contained">
            Create Backup
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
